import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  Chip,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { PlayCircle, Favorite } from '@mui/icons-material';

const shimmer = keyframes`
  0% { background-position: -468px 0; }
  100% { background-position: 468px 0; }
`;

const CleanCard = styled(Card)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 4px 20px rgba(0, 0, 0, 0.3), 0 1px 0 rgba(255, 255, 255, 0.05) inset'
    : '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 0 rgba(255, 255, 255, 0.9) inset',
  backdropFilter: 'blur(12px) saturate(120%)',
  marginBottom: 16,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 0 rgba(255, 255, 255, 0.08) inset'
      : '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 0 rgba(255, 255, 255, 0.95) inset',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
  },
}));

const ShimmerBox = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(to right, rgba(255,255,255,0.05) 8%, rgba(255,255,255,0.15) 18%, rgba(255,255,255,0.05) 33%)'
    : 'linear-gradient(to right, rgba(0,0,0,0.05) 8%, rgba(0,0,0,0.15) 18%, rgba(0,0,0,0.05) 33%)',
  backgroundSize: '800px 104px',
  animation: `${shimmer} 1.5s infinite linear`,
  borderRadius: 8,
  height: 20,
  marginBottom: 8,
}));

const VideoInfo = ({ title, description, isLoading }) => {
  return (
    <CleanCard elevation={0}>
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <PlayCircle sx={{ color: 'primary.main', fontSize: 32 }} />
          <Typography variant="h6" color="text.primary" fontWeight={600}>
            Now Playing
          </Typography>
        </Box>

        {isLoading ? (
          <Box>
            <ShimmerBox sx={{ width: '80%', height: 24, mb: 2 }} />
            <ShimmerBox sx={{ width: '100%', height: 16 }} />
            <ShimmerBox sx={{ width: '60%', height: 16 }} />
          </Box>
        ) : (
          <Box>
            <Typography
              variant="h6"
              color="text.primary"
              gutterBottom
              sx={{
                fontWeight: 600,
                lineHeight: 1.3,
                mb: 2,
              }}
            >
              {title}
            </Typography>
            
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.6, mb: 2 }}
            >
              {description}
            </Typography>

            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip
                icon={<Favorite sx={(theme) => ({ 
                  color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF'
                })} />}
                label="David Dobrik"
                size="medium"
                sx={(theme) => ({
                  bgcolor: 'primary.main',
                  color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                })}
              />
              <Chip
                label="Vlog Squad"
                size="medium"
                variant="outlined"
                sx={{
                  borderColor: 'secondary.main',
                  color: 'secondary.main',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                }}
              />
              <Chip
                label="Entertainment"
                size="medium"
                variant="outlined"
                sx={{
                  borderColor: 'text.secondary',
                  color: 'text.secondary',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                }}
              />
            </Box>
          </Box>
        )}
      </CardContent>
    </CleanCard>
  );
};

export default VideoInfo;